import { useMemo, useState } from 'react';
import { Bot, Plus, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useUsers } from '@/hooks/use-users';
import { useAgents } from '@/hooks/use-agents';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import type { DraftMember } from './draft-member';
import { makeDraftId } from './draft-member';

interface MemberPickerProps {
  /** Channel used to filter agent candidates — backend enforces too. */
  channel: string;
  /** Current draft members — used to exclude already-selected users/agents. */
  members: DraftMember[];
  /** Fires with the new draft to append to the parent's list. */
  onAdd: (member: DraftMember) => void;
}

/**
 * "Add member" control rendered next to the Members header in the team
 * drawer. Users: server-side search (debounced) so we never load the
 * whole org. Agents: client-side filter over the cached list — they're
 * few, and we need the channel-match filter anyway.
 *
 * Entirely stateless about the team itself — adds are returned to the
 * parent, which owns the draft list.
 */
export function MemberPicker({ channel, members, onAdd }: MemberPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 150);
  const trimmedSearch = debouncedSearch.trim();

  const { data: usersData } = useUsers({
    queries: trimmedSearch ? [{ field: '_search', operator: 'like', value: trimmedSearch }] : [],
    limit: 50,
    offset: 0,
  });
  const { data: agentsData } = useAgents();

  const alreadyUserIds = useMemo(
    () => new Set(members.filter((m) => m.userId).map((m) => m.userId!)),
    [members]
  );
  const alreadyAgentIds = useMemo(
    () => new Set(members.filter((m) => m.agentId).map((m) => m.agentId!)),
    [members]
  );

  const addableUsers = (usersData?.items ?? []).filter(
    (u) => u.rowStatus === 0 && !alreadyUserIds.has(u.id)
  );
  const agentSearch = trimmedSearch.toLowerCase();
  const addableAgents = (agentsData ?? []).filter(
    (a) =>
      a.isActive &&
      a.channel === channel &&
      !alreadyAgentIds.has(a.id) &&
      (agentSearch === '' || a.name.toLowerCase().includes(agentSearch))
  );

  const commit = (draft: DraftMember): void => {
    onAdd(draft);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Plus className="mr-1 h-3.5 w-3.5" />
          Add
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="end"
        // The containing <Sheet> locks body scroll + prevents-default
        // wheel events that bubble past its content. This popover is
        // portaled at body level, so its wheel events would otherwise be
        // swallowed; stop propagation here keeps scroll local.
        onWheel={(e) => e.stopPropagation()}
        onWheelCapture={(e) => e.stopPropagation()}
      >
        <Command
          // Users are server-filtered, agents are filtered above — disable
          // Command's built-in filter to avoid double-filtering.
          shouldFilter={false}
        >
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search users and agents..."
          />
          <CommandList className="max-h-[280px] overflow-y-auto">
            <CommandEmpty>No matches.</CommandEmpty>
            {addableUsers.length > 0 && (
              <CommandGroup heading="Users">
                {addableUsers.map((u) => (
                  <CommandItem
                    key={`user-${u.id}`}
                    value={`${u.firstName} ${u.lastName} ${u.email} user`}
                    onSelect={() =>
                      commit({
                        draftId: makeDraftId(),
                        memberType: 'user',
                        userId: u.id,
                        display: {
                          name: `${u.firstName} ${u.lastName}`.trim(),
                          email: u.email,
                        },
                      })
                    }
                  >
                    <User className="mr-2 h-4 w-4 text-muted-foreground" />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm">
                        {u.firstName} {u.lastName}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">{u.email}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {addableAgents.length > 0 && (
              <>
                {addableUsers.length > 0 && <CommandSeparator />}
                <CommandGroup heading="Agents">
                  {addableAgents.map((a) => (
                    <CommandItem
                      key={`agent-${a.id}`}
                      value={`${a.name} agent`}
                      onSelect={() =>
                        commit({
                          draftId: makeDraftId(),
                          memberType: 'agent',
                          agentId: a.id,
                          display: { name: a.name },
                        })
                      }
                    >
                      <Bot className="mr-2 h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{a.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Members" header row with the count + Add button. Extracted so
 * TeamEditor's body stays focused on form orchestration.
 */
export function MemberSectionHeader({
  count,
  children,
}: {
  count: number;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <Label className="gap-1">
        Members
        {count > 0 && (
          <span className="text-[10px] font-normal text-muted-foreground">({count})</span>
        )}
      </Label>
      {children}
    </div>
  );
}
